import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type AvatarSize = 'sm' | 'md';

/**
 * An initial in a beveled tile, standing in for a player or a piece.
 *
 * The initial is derived from `name` rather than accepted directly, so
 * a caller can't drift the letter out of sync with the label beside it.
 *
 * `color` takes any CSS background — the brand's panel gradients
 * (`var(--hh-panel-window)`) as readily as a flat piece color — because
 * its two real uses want different things: the turn owner's tile is a
 * brand panel, while a frog's tile is that frog's actual playing color.
 *
 * Decorative by default: the name it abbreviates is essentially always
 * rendered next to it, so the initial is hidden from assistive tech to
 * avoid reading the same name twice. Pass `label` when it stands alone.
 */
@Component({
  selector: 'hh-avatar',
  standalone: true,
  template: `
    <span
      [class]="classes()"
      [style.background]="color()"
      [attr.aria-hidden]="label() ? null : 'true'"
      [attr.aria-label]="label() || null"
      [attr.role]="label() ? 'img' : null"
      >{{ initial() }}</span
    >
  `,
  styleUrl: './hh-avatar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhAvatarComponent {
  readonly name = input('');
  readonly size = input<AvatarSize>('md');

  /** Any CSS background value; unset uses the brand's window panel. */
  readonly color = input<string | undefined>(undefined);

  /** Set only when no visible name accompanies the tile. */
  readonly label = input('');

  protected readonly initial = computed(() => this.name().trim().charAt(0).toUpperCase());

  protected readonly classes = computed(() => `hh-avatar hh-avatar--${this.size()}`);
}
